import React from 'react';
import { useProductsMap } from '@/hooks/useProductsMap';
import { useProductConnection } from '@/hooks/useProductConnection';
import { ProductScopeStatePanel, errorToPanel } from '@/components/ProductScopeStatePanel';
import { parseLoadError, type ProductIntegrationNeed } from '@/utils/productScope';

type ProductScopeGateProps = {
  productId: string | undefined;
  /** Exige produto selecionado no filtro (abas por produto). */
  requireSelection?: boolean;
  integration?: ProductIntegrationNeed;
  loading?: boolean;
  loadError?: unknown;
  /** Quando true e sem erro, exibe estado “sem dados”. */
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDetail?: string;
  onRetry?: () => void;
  children: React.ReactNode;
};

/**
 * Bloqueia o conteúdo da página/aba até haver produto válido, integração OK e dados (ou vazio explícito).
 */
export const ProductScopeGate: React.FC<ProductScopeGateProps> = ({
  productId,
  requireSelection = true,
  integration = 'core',
  loading = false,
  loadError,
  isEmpty = false,
  emptyTitle = 'Nenhum dado para este produto',
  emptyDetail = 'Não há registros no período ou escopo selecionado. Isso é esperado se o produto ainda não tem clientes ou eventos.',
  onRetry,
  children
}) => {
  const productsById = useProductsMap();
  const pid = productId?.trim() || '';
  const product = pid ? productsById.get(pid) : undefined;
  const connection = useProductConnection(pid || undefined, integration);

  if (requireSelection && !pid) {
    return (
      <ProductScopeStatePanel
        variant="select-product"
        title="Selecione um produto"
        detail="Use o filtro no topo da página para escolher o produto cujo conteúdo deseja visualizar."
        severity="info"
        showRegistryLink={false}
      />
    );
  }

  if (connection.checking) {
    return <ProductScopeStatePanel variant="checking" title="" />;
  }

  const integrationError = connection.syncError || connection.remoteError;
  if (integrationError) {
    const panel = errorToPanel(integrationError);
    return (
      <ProductScopeStatePanel
        variant="blocked"
        productId={pid}
        productName={product?.displayName ?? null}
        title={panel.title}
        detail={panel.detail ?? ''}
        severity={panel.severity ?? 'warning'}
        {...(onRetry ? { onRetry } : {})}
      />
    );
  }

  if (loading) {
    return <ProductScopeStatePanel variant="checking" title="" />;
  }

  if (loadError) {
    const panel = errorToPanel(parseLoadError(loadError));
    return (
      <ProductScopeStatePanel
        variant="blocked"
        productId={pid}
        productName={product?.displayName ?? null}
        title={panel.title}
        detail={panel.detail ?? ''}
        severity={panel.severity ?? 'error'}
        {...(onRetry ? { onRetry } : {})}
      />
    );
  }

  if (isEmpty) {
    return (
      <ProductScopeStatePanel
        variant="empty"
        productId={pid}
        productName={product?.displayName ?? null}
        title={emptyTitle}
        detail={emptyDetail ?? ''}
        showRegistryLink={false}
        {...(onRetry ? { onRetry } : {})}
      />
    );
  }

  return <>{children}</>;
};
